import { useCallback, useState } from 'react';
import { useEffect } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { GlossaryResource, PluginOrderResource } from '~~apis/resource';
import Loading from '~~elements/Loading';
import Form from '~~elements/Form';
import Input from '~~elements/Input';
import Select from '~~elements/Select';
import Textarea from '~~elements/Textarea';
import Tooltip from '~~elements/Tooltip';
import Button from '~~elements/Button';
import toast from '~~elements/Toast';
import { usePluginShopState, usePluginShopDispatch } from './usePluginShop';
import { Cities } from './Glossary';

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  padding-bottom: 2rem;

  .buying {
    width: 980px;
    max-width: 95%;
    display: flex;
    flex-direction: column;
    align-items: center;
    jusitfy-content: start;

    &--title {
      font-size: 1.5rem;
      margin: 1.5rem 0;
      padding: 0;
    }

    &--subtitle {
      font-size: 1.2rem;
      margin: 1rem 0;
      padding: 0;
    }

    &--plugin {
      display: block;
      width: 100%;
      overflow-x: auto;
      overflow-y: hidden;

      table {
        width: 100%;
        border-spacing: 0;
        tbody > tr {
          border-bottom: 1px solid #eee;
        }
        thead > tr {
          border-bottom: 1px solid #eee;
        }

        th {
          white-space: nowrap;
        }

        th,
        td {
          text-align: left;
          margin: 0;
          padding: 0.5rem;
          border-right: 0px;
          :last-child {
            border-right: 0px;
          }

          width: 1%;
          &.collapse {
            width: 0.0000000001%;
          }
          &.max {
            width: 100%;
          }

          > span {
            display: block;
          }
        }

        .nowrap {
          white-space: nowrap;
        }
      }

      .total {
        font-size: 1.1rem;
        text-align: end;
        margin: 1.1rem 0;
        padding: 0;
      }
    }

    &--form {
      width: 100%;
      padding: 0.3rem 0.5rem;
      background-color: #fafafa;
      .description {
        width: 100%;
        padding: 0.3rem 0.5rem;
        margin-bottom: 0.5rem;
        border-left: 2px solid gray;
        text-align: start;
        font-size: 0.9rem;
      }
      label {
        white-space: nowrap;
        margin-right: 0.2rem;
      }
    }
  }
`;

const getInitPluginOrderState = () => ({
  buyer_name: '',
  buyer_phone: '',
  buyer_tel: '',
  buyer_city: Cities.counties[0],
  buyer_postcode: Cities.districts[0][1][0],
  buyer_street: '',
  receipt_type: 'PERSONAL',
  comment: '',
  cityIndex: 0,
  districtIndex: 0,
});

const PurchasePage = () => {
  const { amount, pluginTarget, pluginName } = usePluginShopState();
  const dispatch = usePluginShopDispatch();
  const history = useHistory();

  const [buyingPlugin, setBuyingPlugin] = useState(null);
  const [total, setTotal] = useState(10000);
  const [orderForm, setOrderForm] = useState(getInitPluginOrderState());
  const [apiInProgress, setApiInProgress] = useState(false);

  const pluginNotFound = useCallback(() => {
    dispatch({ type: 'cancel' });
    history.replace(`/shop/plugin`);
  }, [dispatch, history]);

  useEffect(() => {
    if (amount <= 0 || !pluginTarget || !pluginName) {
      pluginNotFound();
      return;
    }

    window.scrollTo(0, 0);
    GlossaryResource.getTargetPlugins({ target: pluginTarget }).then(({ data: apiPlugins }) => {
      if (!apiPlugins[pluginName]) {
        pluginNotFound();
        return;
      }

      setBuyingPlugin(apiPlugins[pluginName]);
      setTotal(apiPlugins[pluginName].retail_price * amount);
    });
  }, [pluginNotFound, amount, pluginTarget, pluginName]);

  const handleInputChange = (e) => {
    setOrderForm({
      ...orderForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleCityChange = (e) => {
    const city = Cities.counties[parseInt(e.target.value)];
    const postcode = Cities.districts[parseInt(e.target.value)][1][0];
    setOrderForm({
      ...orderForm,
      buyer_city: city,
      buyer_postcode: postcode,
      cityIndex: parseInt(e.target.value),
      districtIndex: 0,
    });
  };

  const handleDistrictChange = (e) => {
    const postcode = Cities.districts[orderForm.cityIndex][1][parseInt(e.target.value)];
    setOrderForm({
      ...orderForm,
      buyer_postcode: postcode,
      districtIndex: parseInt(e.target.value),
    });
  };

  const validForm = () => {
    const name = orderForm.buyer_name.trim();
    const phone = orderForm.buyer_phone.trim();
    const street = orderForm.buyer_street.trim();
    return !!name && !!phone && !!street;
  };

  const handleSubmit = () => {
    setApiInProgress(true);
    PluginOrderResource.createPluginOrder({
      data: {
        plugin_target: pluginTarget,
        plugin_key: pluginName,
        amount,
        target_uniqid: '',
        ...orderForm,
        buyer_name: orderForm.buyer_name.trim(),
        buyer_phone: orderForm.buyer_phone.trim(),
        buyer_tel: orderForm.buyer_tel.trim(),
        buyer_street: orderForm.buyer_street.trim(),
        comment: orderForm.comment.trim(),
      },
    })
      .then((ecpayHTMLString) => {
        console.log(ecpayHTMLString);
        document.open();
        document.write(ecpayHTMLString);
        document.close();
      })
      .catch((e) => {
        toast(`發生錯誤，請稍後再試(${e.toString()})`, { status: 'error', second: 2 });
      })
      .finally(() => {
        setApiInProgress(false);
      });
  };

  return (
    <StyledDiv>
      {!buyingPlugin ? (
        <Loading />
      ) : (
        <div className="buying">
          <h1 className="buying--title">擴充套件訂單確認</h1>
          <div className="buying--plugin">
            <table>
              <thead>
                <tr>
                  <th className="max">商品名稱</th>
                  <th className="collapse nowrap">單價</th>
                  <th className="collapse nowrap">數量</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{buyingPlugin.name}</td>
                  <td className="nowrap">
                    {`NT$ `}
                    {new Intl.NumberFormat('zh-TW').format(buyingPlugin.retail_price)}
                  </td>
                  <td className="nowrap">{amount}</td>
                </tr>
              </tbody>
            </table>
            <h3 className="total">
              {`總價： NT$ `}
              {new Intl.NumberFormat('zh-TW').format(total)}
            </h3>
          </div>
          <div className="buying--form">
            <h2 className="buying--subtitle">付款資訊</h2>
            <div className="description">
              發票將於付款完成後盡速送出，如需打統編請於備註欄補充說明。
              <br />
              註：本商品不包含任何實體產品，購買前請先確認商品成果範例符合您的需求。
            </div>
            <Form onSubmit={() => {}}>
              <div className="box">
                <div className="row --multi-input-field">
                  <div className="input-field">
                    <label>全名</label>
                    <Input
                      width="10rem"
                      name="buyer_name"
                      value={orderForm.buyer_name}
                      onChange={handleInputChange}
                      textAlign="start"
                      disabled={apiInProgress}
                    />
                  </div>
                  <div className="input-field">
                    <label>手機</label>
                    <Input
                      width="10rem"
                      name="buyer_phone"
                      value={orderForm.buyer_phone}
                      onChange={handleInputChange}
                      textAlign="start"
                      disabled={apiInProgress}
                    />
                  </div>
                  <div className="input-field">
                    <label>市話</label>
                    <Input
                      width="10rem"
                      name="buyer_tel"
                      value={orderForm.buyer_tel}
                      onChange={handleInputChange}
                      placeholder="選填 (xx)-xxxxxxxx"
                      textAlign="start"
                      disabled={apiInProgress}
                    />
                  </div>
                </div>
                <label>發票地址</label>
                <div className="input-field">
                  <div className="input-list">
                    <Select
                      width="fit-content"
                      name="cityIndex"
                      value={orderForm.cityIndex}
                      onChange={handleCityChange}
                      disabled={apiInProgress}
                    >
                      {Cities.counties.map((country, cIndex) => (
                        <option key={cIndex} value={cIndex}>
                          {country}
                        </option>
                      ))}
                    </Select>
                    <Select
                      width="fit-content"
                      name="districtIndex"
                      value={orderForm.districtIndex}
                      onChange={handleDistrictChange}
                      disabled={apiInProgress}
                    >
                      {Cities.districts[orderForm.cityIndex][0].map((district, dIndex) => (
                        <option key={dIndex} value={dIndex}>
                          {district}
                        </option>
                      ))}
                    </Select>
                    <Input
                      name="buyer_street"
                      width="20rem"
                      textAlign="start"
                      placeholder="地址"
                      value={orderForm.buyer_street}
                      onChange={handleInputChange}
                      disabled={apiInProgress}
                    />
                  </div>
                </div>
                <label>發票型態</label>
                <div className="input-field">
                  <div className="input-list">
                    <label>
                      <input
                        type="radio"
                        name="receipt_type"
                        value="PERSONAL"
                        checked={orderForm.receipt_type === 'PERSONAL'}
                        onChange={handleInputChange}
                        disabled={apiInProgress}
                      />
                      個人發票
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="receipt_type"
                        value="DONATE"
                        checked={orderForm.receipt_type === 'DONATE'}
                        onChange={handleInputChange}
                        disabled={apiInProgress}
                      />
                      捐贈發票
                      <Tooltip content="目前捐贈單位：球芽基金" />
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="receipt_type"
                        value="COMPANY"
                        checked={orderForm.receipt_type === 'COMPANY'}
                        onChange={handleInputChange}
                        disabled={apiInProgress}
                      />
                      公司發票
                      <Tooltip content="請於備註填寫公司統編" />
                    </label>
                  </div>
                </div>
                <label>備註{orderForm.receipt_type === 'COMPANY' && <i>*請備註公司統編</i>}</label>
                <div className="input-field">
                  <Textarea
                    name="comment"
                    value={orderForm.comment}
                    onChange={handleInputChange}
                    disabled={apiInProgress}
                  />
                </div>
              </div>
            </Form>
            <div className="description">
              確定送出後將引導您至站外付款頁面，請依指示填寫付款資訊以完成商品購買流程。
            </div>
            <Button width="10rem" disabled={apiInProgress || !validForm()} onClick={handleSubmit}>
              確定送出
            </Button>
          </div>
        </div>
      )}
    </StyledDiv>
  );
};

export default PurchasePage;
